import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Lock, ShieldCheck, Linkedin, ChevronDown, BarChart3, Target, Layers, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

const PRICE = "99 ريال";
const FONT_AR = "'Tajawal','IBM Plex Sans Arabic',-apple-system,system-ui,sans-serif";
const FONT_DISPLAY = "'Tajawal','IBM Plex Sans Arabic',serif";

const ensureFonts = () => {
  if (document.getElementById("course-font")) return;
  const l = document.createElement("link");
  l.id = "course-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800;900&display=swap";
  document.head.appendChild(l);
};

const vid = () => {
  try {
    let v = localStorage.getItem("course_vid");
    if (!v) {
      v = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now()}`;
      localStorage.setItem("course_vid", v);
    }
    return v;
  } catch (_) { return "anon"; }
};
const track = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: vid(), page: "course" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/course/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    else api.post("/course/track", JSON.parse(payload));
  } catch (_) { /* analytics never breaks the funnel */ }
};

// Google Ads "Add to cart" — fires synchronously before Stripe redirect.
const fireGtagATC = () => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    if (window.__course_atc_fired) return;
    window.__course_atc_fired = true;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
      value: 1.0,
      currency: "EGP",
    });
  } catch (_) { /* analytics never breaks the funnel */ }
};

// TikTok InitiateCheckout — fired alongside Google Ads ATC.
const fireTikTokATC = () => {
  try {
    if (typeof window === "undefined" || typeof window.ttq?.track !== "function") return;
    window.ttq.track("InitiateCheckout", {
      contents: [{ content_id: "perf_ads_os", content_type: "product", content_name: "Performance Ads OS", price: 99, quantity: 1 }],
      value: 99,
      currency: "SAR",
    });
  } catch (_) { /* analytics never breaks the funnel */ }
};

const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C9A66A]/[0.10] border border-[#C9A66A]/35 text-[11.5px] tracking-wide text-[#E7C98C] font-bold">
    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A66A] animate-pulse" /> {children}
  </span>
);

const CTAButton = ({ children, onClick, loading, large, testid }) => (
  <button
    onClick={onClick}
    disabled={loading}
    data-testid={testid}
    className={`inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0F1C] font-extrabold tracking-wide rounded-full transition-all disabled:opacity-60 shadow-[0_12px_44px_-8px_rgba(201,166,106,0.55)] ${large ? "text-[16px] px-9 py-4 min-h-[58px]" : "text-[14.5px] px-7 py-3.5 min-h-[52px]"}`}
    style={{ fontFamily: FONT_AR }}
  >
    {loading ? "جاري فتح الدفع…" : <>{children} <ArrowLeft className="w-4 h-4" /></>}
  </button>
);

const SectionEyebrow = ({ children }) => (
  <div className="text-[11.5px] tracking-[0.22em] text-[#C9A66A] font-bold mb-3">{children}</div>
);

const Card = ({ children, accent, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl p-6 transition-all ${accent ? "bg-gradient-to-b from-[#15203A] to-[#0E162B] border border-[#C9A66A]/40 shadow-[0_0_50px_-18px_rgba(201,166,106,0.4)]" : "bg-[#0E162B] border border-white/[0.07] hover:border-[#C9A66A]/25"}`}
  >
    {children}
  </div>
);

export default function LandingCourse() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [faqOpen, setFaqOpen] = useState(null);

  useEffect(() => {
    ensureFonts();
    document.title = "Performance Ads OS | نظام تشغيل الإعلانات المدفوعة";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "نظام عملي يساعدك على إطلاق حملات إعلانية مدفوعة، قراءة الأرقام، واتخاذ قرار واضح: توقف، تصلح، أو توسّع.");
    track("page_view");
    try { if (window.ttq?.track) window.ttq.track("ViewContent", { contents: [{ content_id: "perf_ads_os", content_type: "product", content_name: "Performance Ads OS", price: 99, quantity: 1 }], value: 99, currency: "SAR" }); } catch (_) { /* never breaks */ }
  }, []);

  useEffect(() => {
    const start = Date.now();
    let sent = false;
    const send = () => {
      if (sent) return;
      const seconds = Math.round((Date.now() - start) / 1000);
      if (seconds < 2) return;
      sent = true;
      track("time_on_site", { seconds });
    };
    const onHide = () => { if (document.visibilityState === "hidden") send(); };
    window.addEventListener("pagehide", send);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", send);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, []);

  const startCheckout = async (where = "primary") => {
    if (loading) return;
    setErr("");
    track("cta_click", { where });
    fireGtagATC();
    fireTikTokATC();
    // Navigate to dedicated checkout page that collects email + platform survey
    // before creating the Stripe Checkout session. Backend now requires email.
    setLoading(true);
    try {
      navigate("/perfads/checkout");
    } finally {
      // Don't keep spinner spinning if navigation race-conditions
      setTimeout(() => setLoading(false), 600);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="min-h-screen bg-[#070C18] text-[#F0EAD9] selection:bg-[#C9A66A]/40"
      style={{ fontFamily: FONT_AR, scrollBehavior: "smooth" }}
      data-testid="course-landing-root"
    >
      {/* HEADER */}
      <nav className="sticky top-0 z-30 bg-[#070C18]/85 backdrop-blur-md border-b border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5" data-testid="course-logo">
            <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center font-extrabold text-[#0A0F1C] text-[14px]">P</span>
            <div className="leading-tight">
              <div className="font-extrabold text-[14px] tracking-tight">Performance Ads OS</div>
              <div className="text-[10px] text-[#A7956E]">نظام تشغيل الإعلانات المدفوعة</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-7 text-[13px] text-[#D6CCAF]">
            <button onClick={() => scrollTo("learn")} className="hover:text-[#C9A66A] transition-colors">ماذا ستتعلم؟</button>
            <button onClick={() => scrollTo("curriculum")} className="hover:text-[#C9A66A] transition-colors">محتوى المنتج</button>
            <button onClick={() => scrollTo("instructor")} className="hover:text-[#C9A66A] transition-colors">عن المدرّب</button>
            <button onClick={() => scrollTo("price")} className="hover:text-[#C9A66A] transition-colors">السعر</button>
          </div>
          <CTAButton testid="course-nav-cta" onClick={() => startCheckout("nav")} loading={loading}>ابدأ الآن</CTAButton>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative overflow-hidden px-5 pt-12 sm:pt-20 pb-16 sm:pb-24" data-testid="course-hero">
        <div className="absolute -top-32 -left-32 w-[460px] h-[460px] rounded-full bg-[#C9A66A]/[0.10] blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute top-40 -right-40 w-[360px] h-[360px] rounded-full bg-[#8A6E3D]/[0.12] blur-3xl pointer-events-none" aria-hidden />
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center relative">
          <div>
            <Badge>للأصحاب المشاريع والمسوقين الذين يشغلون إعلانات مدفوعة</Badge>
            <h1 className="mt-6 font-extrabold text-[32px] sm:text-5xl lg:text-[54px] leading-[1.18] tracking-tight" style={{ fontFamily: FONT_DISPLAY }} data-testid="course-hero-title">
              لا تزيد <span className="bg-gradient-to-l from-[#E2C281] to-[#C9A66A] bg-clip-text text-transparent">ميزانية الإعلان</span> قبل أن تفهم الأرقام.
            </h1>
            <p className="mt-5 text-[15.5px] sm:text-[17px] text-[#CFC3A1] leading-[1.85] max-w-[600px]">
              نظام عملي يساعدك على إطلاق الحملات المدفوعة، قراءة نتائجها، ومعرفة متى توقف، تصلح، أو توسّع — بدون حرق الميزانية في قرارات عشوائية.
            </p>
            <ul className="mt-7 space-y-3 text-[14.5px] text-[#E7DDC0]">
              {[
                "افهم الفرق بين الإعلان الضعيف والصفحة الضعيفة",
                "اقرأ أرقام CPM, CTR, CPC, CPA, ROAS بدون ارتباك",
                "اعرف هل المشكلة في الإعلان، الجمهور، العرض، السعر، أو الصفحة",
                "استخدم قوالب عملية قبل وأثناء وبعد الإطلاق",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`course-hero-bullet-${i}`}>
                  <Check className="w-5 h-5 text-[#C9A66A] shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <CTAButton large onClick={() => startCheckout("hero")} loading={loading} testid="course-hero-cta">
                ابدأ الآن — {PRICE}
              </CTAButton>
              <div className="mt-3 text-[12.5px] text-[#A7956E] flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-[#C9A66A]" /> وصول مباشر إلى الدروس والقوالب بعد الشراء
              </div>
              {err && <div className="mt-3 text-[13px] text-[#F0838F]" data-testid="course-hero-err">{err}</div>}
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative" data-testid="course-hero-visual">
            <div className="relative bg-gradient-to-b from-[#13203F] to-[#0A1226] border border-[#C9A66A]/30 rounded-3xl p-6 shadow-[0_30px_80px_-25px_rgba(201,166,106,0.4)]">
              <div className="text-[10.5px] tracking-[0.22em] text-[#C9A66A] font-bold mb-4">PERFORMANCE ADS OS</div>
              <div className="font-extrabold text-[20px] text-white" style={{ fontFamily: FONT_DISPLAY }}>نظام تشغيل الإعلانات المدفوعة</div>
              <div className="mt-2 text-[12px] text-[#A7956E]">6 دروس + Workbook + قوالب قرار</div>
              <div className="mt-5 grid grid-cols-3 gap-2.5">
                {[
                  { l: "CTR", v: "2.1%" },
                  { l: "CPA", v: "12.4 ﷼" },
                  { l: "ROAS", v: "3.4×" },
                  { l: "CPC", v: "0.58 ﷼" },
                  { l: "CPM", v: "8.20 ﷼" },
                  { l: "CVR", v: "1.8%" },
                ].map((m) => (
                  <div key={m.l} className="bg-white/[0.025] border border-white/[0.06] rounded-lg px-2.5 py-2.5">
                    <div className="text-[9.5px] tracking-[0.18em] text-[#A7956E] font-bold">{m.l}</div>
                    <div className="text-[15px] font-bold text-white tabular-nums mt-0.5">{m.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center text-[11.5px] font-bold">
                <div className="rounded-md bg-[#F87171]/[0.12] border border-[#F87171]/30 text-[#FCA5A5] py-2">توقف</div>
                <div className="rounded-md bg-[#FBBF24]/[0.12] border border-[#FBBF24]/30 text-[#FDE68A] py-2">أصلح</div>
                <div className="rounded-md bg-[#C9A66A]/[0.18] border border-[#C9A66A]/45 text-[#E7C98C] py-2">وسّع</div>
              </div>
            </div>
            <div className="absolute -bottom-3 right-6 bg-[#0A0F1C] border border-[#C9A66A]/40 rounded-md px-3 py-1.5 text-[10.5px] tracking-[0.2em] text-[#C9A66A] font-bold">
              توقف · أصلح · وسّع
            </div>
          </div>
        </div>
      </header>

      {/* PAIN */}
      <section className="px-5 py-16 sm:py-20 border-t border-white/[0.05]" data-testid="course-pain">
        <div className="max-w-5xl mx-auto">
          <SectionEyebrow>المشكلة ليست في المنصة فقط</SectionEyebrow>
          <h2 className="font-extrabold text-[26px] sm:text-[38px] leading-[1.2] tracking-tight max-w-3xl" style={{ fontFamily: FONT_DISPLAY }}>
            أغلب الحملات <span className="text-[#C9A66A]">تخسر قبل أن تبدأ.</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#CFC3A1] leading-[1.9] max-w-3xl">
            كثير من الناس يبدأ الحملة، يختار الهدف، يرفع الإعلان، ثم ينتظر النتائج. بعد يومين تبدأ الأسئلة: هل المشكلة في الجمهور؟ هل أغير الإعلان؟ هل أزيد الميزانية؟ هل أوقف الحملة؟
          </p>
          <p className="mt-4 text-[15px] text-white leading-[1.9] max-w-3xl font-semibold">
            الحقيقة أن أغلب الخسارة تأتي من غياب نظام قرار واضح.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {[
              { t: "نقرات بدون مبيعات", d: "الإعلان يحصل على ضغطات، لكن الصفحة لا تحول. فتعتقد أن المشكلة في الجمهور بينما الخلل بعد الضغط." },
              { t: "أرقام كثيرة بدون معنى", d: "ترى CPM وCTR وCPC وCPA وROAS، لكن لا تعرف أي رقم يقود القرار." },
              { t: "توسعة قبل الفهم", d: "تزيد الميزانية لأن الحملة \u201cشكلها تتحسن\u201d، ثم تكتشف أنك تدفع أكثر لنفس الخطأ." },
              { t: "اعتماد كامل على الوكالة أو الفريلانسر", d: "لا تعرف كيف تسأل، كيف تراجع، أو كيف تحكم على الأداء الحقيقي." },
            ].map((c, i) => (
              <Card key={i} testid={`course-pain-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 shrink-0 rounded-md bg-[#C9A66A]/[0.12] border border-[#C9A66A]/30 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-[#C9A66A]" strokeWidth={2} />
                  </span>
                  <div>
                    <div className="font-bold text-[16px] text-white">{c.t}</div>
                    <p className="mt-2 text-[13.5px] text-[#CFC3A1] leading-[1.85]">{c.d}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT EXPLANATION */}
      <section className="px-5 py-16 sm:py-20 bg-[#080E1F] border-y border-white/[0.05]" data-testid="course-product-explanation">
        <div className="max-w-4xl mx-auto">
          <SectionEyebrow>ما هو Performance Ads OS؟</SectionEyebrow>
          <h2 className="font-extrabold text-[24px] sm:text-[34px] leading-[1.25] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            ليس كورس تسويق شامل. <br />
            <span className="text-[#C9A66A]">هذا نظام عملي لاتخاذ قرارات الإعلانات.</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#CFC3A1] leading-[1.9]">
            Performance Ads OS مصمم ليعطيك طريقة واضحة لفهم الحملات المدفوعة من زاوية القرار: ماذا تجهز قبل الإطلاق، ماذا تقرأ بعد الإطلاق، وكيف تحدد الخطوة التالية بدون تخمين.
          </p>
          <div className="mt-8 bg-gradient-to-b from-[#15203A] to-[#0E162B] border-r-2 border-[#C9A66A] rounded-2xl p-6">
            <p className="text-[15px] text-[#E7DDC0] leading-[1.85]">
              بدل أن تسأل: <span className="text-white font-semibold">"هل الإعلان جيد؟"</span>
            </p>
            <p className="mt-2 text-[15px] text-[#E7DDC0] leading-[1.85]">
              ستتعلم أن تسأل: <span className="text-[#C9A66A] font-bold">"أين انكسرت السلسلة؟ الإعلان، الجمهور، العرض، الصفحة، السعر، أم التتبع؟"</span>
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOU WILL LEARN */}
      <section id="learn" className="px-5 py-16 sm:py-20" data-testid="course-learn">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-extrabold text-[26px] sm:text-[38px] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            بعد المنتج، ستكون <span className="text-[#C9A66A]">قادرًا على:</span>
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "تجهيز الحملة قبل الإطلاق", d: "تعرف ما الذي يجب أن يكون واضحًا قبل صرف أي ريال: المنتج، السعر، الجمهور، الوعد، الاعتراض، والرقم المقبول.", i: Target },
              { t: "اختيار الهدف الصحيح", d: "تفهم متى تستخدم Sales، Leads، Traffic، Engagement، أو Messages حسب النتيجة المطلوبة.", i: Layers },
              { t: "قراءة الأرقام المهمة", d: "تفهم CPM, CTR, CPC, CVR, CPA, ROAS بطريقة عملية مرتبطة بالقرار.", i: BarChart3 },
              { t: "تشخيص مكان المشكلة", d: "تعرف هل الخلل قبل الضغط أو بعد الضغط، وهل تحتاج إعلانًا جديدًا أو صفحة أفضل أو عرضًا أوضح.", i: AlertTriangle },
              { t: "اختبار Creatives بدون فوضى", d: "تتعلم كيف تختبر الزوايا الإعلانية بدل تغيير كل شيء في نفس الوقت.", i: Layers },
              { t: "قرار الإيقاف أو الإصلاح أو التوسعة", d: "تستخدم Framework واضح لاتخاذ القرار بعد 24، 48، و72 ساعة.", i: ShieldCheck },
            ].map((c, i) => (
              <Card key={i} testid={`course-learn-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className="w-10 h-10 rounded-md bg-[#C9A66A]/[0.12] border border-[#C9A66A]/30 flex items-center justify-center">
                    <c.i className="w-4.5 h-4.5 text-[#C9A66A]" strokeWidth={1.8} />
                  </span>
                  <span className="text-[11px] text-[#7A6F50] font-bold">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="font-bold text-[16px] text-white">{c.t}</div>
                <p className="mt-2 text-[13.5px] text-[#CFC3A1] leading-[1.85]">{c.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="px-5 py-16 sm:py-20 bg-[#080E1F] border-y border-white/[0.05]" data-testid="course-what-you-get">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-extrabold text-[26px] sm:text-[38px] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            ماذا <span className="text-[#C9A66A]">تحصل عليه؟</span>
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {[
              "6 دروس قصيرة وواضحة",
              "Workbook عملي",
              "Campaign Brief Template",
              "Pre-Launch Checklist",
              "Creative Testing Matrix",
              "Metrics Dashboard",
              "Campaign Diagnosis Sheet",
              "7-Day Decision Framework",
            ].map((t, i) => (
              <div key={i} className="bg-[#0E162B] border border-white/[0.07] rounded-xl px-4 py-3.5 flex items-center gap-3" data-testid={`course-get-${i}`}>
                <Check className="w-5 h-5 text-[#C9A66A] shrink-0" strokeWidth={2.5} />
                <span className="text-[14px] text-[#E7DDC0]">{t}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 text-[13px] text-[#A7956E] text-center">مصمم ليكون مختصرًا ومباشرًا. لا وقت ضائع في شرح نظري طويل.</div>
          <div className="mt-8 flex justify-center">
            <CTAButton testid="course-get-cta" onClick={() => startCheckout("what-you-get")} loading={loading} large>
              ابدأ الآن — {PRICE}
            </CTAButton>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="px-5 py-16 sm:py-20" data-testid="course-curriculum">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-extrabold text-[26px] sm:text-[38px] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            محتوى <span className="text-[#C9A66A]">المنتج</span>
          </h2>
          <div className="mt-8 space-y-3">
            {[
              { t: "لماذا تخسر الحملات قبل أن تبدأ؟", d: "تفهم كيف تؤثر قرارات المنتج، السعر، العرض، والصفحة على نتيجة الإعلان قبل تشغيله." },
              { t: "هدف الحملة ليس اختيارًا عشوائيًا", d: "تعرف كيف تختار Campaign Objective بناءً على السلوك المطلوب، لا بناءً على أرخص رقم." },
              { t: "تركيب الحملة: العرض، الجمهور، الإعلان، الصفحة", d: "تفهم الحملة كسلسلة كاملة، وتتعلم كيف تحدد الحلقة الضعيفة." },
              { t: "قراءة الأرقام: CPM, CTR, CPC, CVR, CPA, ROAS", d: "شرح عملي لأهم الأرقام وكيف تقرأها معًا لاتخاذ قرار واضح." },
              { t: "اختبار الإعلانات: كيف تعرف أي Creative يستحق الميزانية؟", d: "طريقة بسيطة لاختبار الزوايا الإعلانية بدون فوضى أو استنتاجات خاطئة." },
              { t: "قرار الحملة: أوقف، أصلح، أو وسّع", d: "Framework عملي لمراجعة الحملة بعد الإطلاق واتخاذ القرار الصحيح." },
            ].map((l, i) => (
              <div key={i} className="bg-[#0E162B] border border-white/[0.07] rounded-xl px-5 py-4 hover:border-[#C9A66A]/30 transition-colors" data-testid={`course-lesson-${i + 1}`}>
                <div className="flex items-start gap-4">
                  <span className="text-[#C9A66A] font-extrabold text-[18px] tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-bold text-[15.5px] text-white">{l.t}</div>
                    <p className="mt-1.5 text-[13.5px] text-[#CFC3A1] leading-[1.8]">{l.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTOR */}
      <section id="instructor" className="px-5 py-16 sm:py-20 bg-[#080E1F] border-y border-white/[0.05]" data-testid="course-instructor">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[280px_1fr] gap-10 items-start">
          <div className="relative mx-auto md:mx-0">
            <div className="absolute -inset-3 bg-[#C9A66A]/15 blur-2xl rounded-full pointer-events-none" aria-hidden />
            <img
              src="/images/ahmed-assem.jpg"
              alt="Ahmed Assem Mostafa"
              onError={(e) => { e.target.style.display = "none"; e.target.nextElementSibling.style.display = "flex"; }}
              className="relative w-[220px] h-[220px] md:w-[260px] md:h-[260px] rounded-2xl object-cover border border-[#C9A66A]/40 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)]"
            />
            <div className="relative w-[220px] h-[220px] md:w-[260px] md:h-[260px] rounded-2xl border border-[#C9A66A]/40 bg-gradient-to-br from-[#13203F] to-[#0A1226] items-center justify-center text-[#C9A66A] font-extrabold text-[64px]" style={{ display: "none" }}>
              AA
            </div>
          </div>
          <div>
            <SectionEyebrow>عن المدرّب</SectionEyebrow>
            <h2 className="font-extrabold text-[28px] sm:text-[34px] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>Ahmed Assem Mostafa</h2>
            <div className="text-[14px] text-[#C9A66A] mt-1.5 tracking-wide">Digital &amp; Performance Marketing / Growth Leader</div>
            <p className="mt-5 text-[14.5px] text-[#CFC3A1] leading-[1.9]">
              أحمد عاصم مصطفى لديه أكثر من 16 سنة خبرة في التسويق الرقمي، الإعلانات المدفوعة، النمو، الاكتساب، CRM، والـ full-funnel performance marketing، مع خبرة قوية في قطاعات fintech والبنوك والأسواق الرقمية.
            </p>
            <p className="mt-3 text-[14.5px] text-[#CFC3A1] leading-[1.9]">
              هذا المنتج مبني على طريقة تفكير عملية من داخل التشغيل الحقيقي للحملات، وليس على شرح نظري أو نقل خطوات من المنصات.
            </p>
            <ul className="mt-6 space-y-2.5 text-[13.5px] text-[#E7DDC0]">
              {[
                "16+ سنة خبرة في Digital & Performance Marketing",
                "خبرة في بناء وتشغيل حملات اكتساب ونمو على نطاق واسع",
                "خبرة في fintech، banking، acquisition، retention، CRM، وlifecycle marketing",
                "تركيز عملي على قراءة الأرقام واتخاذ القرار، لا حفظ المصطلحات",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#C9A66A] shrink-0 mt-1" strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://www.linkedin.com/in/ahmedassem"
              target="_blank"
              rel="noreferrer"
              data-testid="course-linkedin-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#0E162B] border border-[#C9A66A]/35 hover:border-[#C9A66A] text-[#E7C98C] font-bold text-[13.5px] px-5 py-3 rounded-full transition-colors"
            >
              <Linkedin className="w-4 h-4" /> مشاهدة حساب LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* WHO FOR / NOT FOR */}
      <section className="px-5 py-16 sm:py-20" data-testid="course-audience">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5">
          <Card accent>
            <h3 className="font-extrabold text-[20px] mb-5" style={{ fontFamily: FONT_DISPLAY }}>
              هذا المنتج <span className="text-[#C9A66A]">مناسب لك إذا كنت:</span>
            </h3>
            <ul className="space-y-3 text-[14px] text-[#E7DDC0]">
              {[
                "صاحب مشروع وتشغل إعلانات بنفسك",
                "صاحب متجر إلكتروني وتريد فهم لماذا الحملات لا تبيع",
                "مسوّق مبتدئ يريد فهم الأداء لا حفظ الأزرار",
                "فريلانسر يريد تشخيص الحملات وشرح قراراته للعملاء",
                "موظف تسويق يتعامل مع وكالة ويريد قراءة الأرقام بثقة",
                "شخص بدأ يصرف على الإعلانات ولا يريد اتخاذ قرارات عشوائية",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2.5"><Check className="w-4.5 h-4.5 text-[#C9A66A] shrink-0 mt-0.5" strokeWidth={2.5} /> <span>{b}</span></li>
              ))}
            </ul>
          </Card>
          <Card>
            <h3 className="font-extrabold text-[20px] mb-5" style={{ fontFamily: FONT_DISPLAY }}>
              غير مناسب إذا كنت <span className="text-[#7A6F50]">تبحث عن:</span>
            </h3>
            <ul className="space-y-3 text-[14px] text-[#A7956E]">
              {[
                "شهادة أو اعتماد أكاديمي",
                "كورس شامل في كل مجالات التسويق الرقمي",
                "وعد مضمون بالمبيعات",
                "شرح تفصيلي لكل زر في كل منصة",
                "اختصارات سحرية بدون اختبار وقراءة أرقام",
                "محتوى عام عن التسويق بدون تطبيق عملي",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2.5"><span className="w-4.5 h-4.5 rounded-full border border-[#7A6F50] shrink-0 mt-0.5 flex items-center justify-center"><span className="block w-2 h-0.5 bg-[#7A6F50]" /></span> <span>{b}</span></li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* PRICE */}
      <section id="price" className="px-5 py-20 sm:py-24 bg-[#080E1F] border-y border-white/[0.05]" data-testid="course-price-section">
        <div className="max-w-4xl mx-auto text-center">
          <SectionEyebrow>نسخة MVP بسعر الإطلاق</SectionEyebrow>
          <h2 className="font-extrabold text-[26px] sm:text-[40px] leading-[1.22] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            ابدأ بنظام واضح <br className="sm:hidden"/> قبل أن تصرف <span className="text-[#C9A66A]">ميزانية أكبر.</span>
          </h2>
          <div className="mt-12 max-w-md mx-auto bg-gradient-to-b from-[#15203A] to-[#0E162B] border border-[#C9A66A]/45 rounded-3xl p-8 shadow-[0_30px_80px_-25px_rgba(201,166,106,0.45)]" data-testid="course-price-card">
            <div className="text-[10.5px] tracking-[0.26em] text-[#C9A66A] font-bold">PERFORMANCE ADS OS</div>
            <div className="mt-3 flex items-baseline justify-center gap-1.5" dir="ltr">
              <span className="text-6xl font-extrabold text-white tabular-nums" style={{ fontFamily: FONT_DISPLAY }}>99</span>
              <span className="text-[#C9A66A] text-[15px] font-bold">SAR</span>
            </div>
            <div className="text-[12px] text-[#A7956E] mt-1">دفعة واحدة</div>
            <div className="mt-6 text-[13px] text-[#A7956E] font-bold tracking-wide">يشمل:</div>
            <ul className="mt-3 text-[13.5px] text-[#E7DDC0] space-y-2.5 text-right">
              {["6 دروس قصيرة", "Workbook عملي", "قوالب تشغيل وتشخيص", "Framework لاتخاذ قرار الحملة خلال 7 أيام"].map((t, i) => (
                <li key={i} className="flex items-center gap-2.5"><Check className="w-4 h-4 text-[#C9A66A]" strokeWidth={2.5}/> {t}</li>
              ))}
            </ul>
            <div className="mt-7">
              <CTAButton testid="course-price-cta" onClick={() => startCheckout("price-card")} loading={loading} large>
                احصل على Performance Ads OS الآن
              </CTAButton>
            </div>
            <div className="mt-3 text-[11.5px] text-[#A7956E] flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3 text-[#C9A66A]" /> دفعة واحدة · وصول مباشر بعد الشراء
            </div>
          </div>
          <div className="mt-10 max-w-2xl mx-auto bg-[#0E162B] border-r-2 border-[#C9A66A] rounded-xl p-5 text-right">
            <p className="text-[14px] text-[#E7DDC0] leading-[1.85]">
              ميزانية إعلان واحدة قد تضيع في يوم. <br/>
              <span className="text-white font-semibold">هذا النظام يساعدك أن تفهم أين تذهب الميزانية قبل أن تزيدها.</span>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-5 py-16 sm:py-20" data-testid="course-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-extrabold text-[26px] sm:text-[38px] tracking-tight text-center" style={{ fontFamily: FONT_DISPLAY }}>أسئلة شائعة</h2>
          <div className="mt-10 space-y-3">
            {[
              { q: "هل هذا كورس للمبتدئين؟", a: "نعم، لكنه ليس سطحيًا. تم تصميمه ليشرح الحملات المدفوعة بطريقة عملية مرتبطة بالأرقام والقرار." },
              { q: "هل أحتاج خبرة سابقة؟", a: "لا تحتاج خبرة متقدمة. يكفي أن تكون مهتمًا بتشغيل الإعلانات أو فهم نتائجها." },
              { q: "هل يشرح Meta وTikTok وGoogle؟", a: "المنتج لا يركز على شرح أزرار منصة واحدة. يركز على المنطق الذي تحتاجه في أي منصة: الهدف، العرض، الجمهور، الإعلان، الأرقام، والقرار." },
              { q: "هل يضمن نتائج؟", a: "لا. لا يوجد منتج محترم يضمن نتائج إعلانية. المنتج يعطيك نظامًا أفضل للفهم، التشخيص، واتخاذ القرار." },
              { q: "هل يناسب أصحاب المشاريع؟", a: "نعم، خصوصًا إذا كنت لا تريد الاعتماد الكامل على وكالة أو فريلانسر بدون فهم ما يحدث." },
              { q: "هل يناسب الفريلانسرز؟", a: "نعم، لأنه يساعدك على تشخيص الحملات وشرح قراراتك للعملاء بطريقة أوضح." },
              { q: "كم مدة المحتوى؟", a: "المنتج مختصر ومباشر. حوالي 90 إلى 120 دقيقة مع Workbook وقوالب عملية." },
            ].map((f, i) => (
              <div key={i} className="bg-[#0E162B] border border-white/[0.07] rounded-xl overflow-hidden" data-testid={`course-faq-${i}`}>
                <button
                  onClick={() => { setFaqOpen(faqOpen === i ? null : i); if (faqOpen !== i) track("faq_open", { idx: i }); }}
                  className="w-full px-5 py-4 flex items-center justify-between gap-3 text-right hover:bg-white/[0.02] transition-colors"
                >
                  <span className="font-bold text-[14.5px] text-white">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 text-[#C9A66A] shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                </button>
                {faqOpen === i && (
                  <div className="px-5 pb-4 text-[13.5px] text-[#CFC3A1] leading-[1.85]">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 py-20 sm:py-24 bg-gradient-to-b from-[#080E1F] to-[#070C18] border-t border-white/[0.05]" data-testid="course-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-extrabold text-[28px] sm:text-[42px] leading-[1.18] tracking-tight" style={{ fontFamily: FONT_DISPLAY }}>
            لا تدير إعلانًا <span className="text-[#C9A66A]">بإحساسك.</span>
          </h2>
          <p className="mt-5 text-[15px] text-[#CFC3A1] leading-[1.9] max-w-xl mx-auto">
            تعلم كيف تقرأ الأرقام، تشخص المشكلة، وتتخذ القرار: <span className="text-white font-semibold">توقف، تصلح، أو توسّع.</span>
          </p>
          <div className="mt-10">
            <CTAButton testid="course-final-cta-btn" onClick={() => startCheckout("final")} loading={loading} large>
              ابدأ الآن — {PRICE}
            </CTAButton>
          </div>
          <div className="mt-4 text-[12px] text-[#A7956E]">Performance Ads OS — نظام تشغيل الإعلانات المدفوعة</div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10 border-t border-white/[0.06] text-center" data-testid="course-footer">
        <div className="font-extrabold text-[16px] text-white" style={{ fontFamily: FONT_DISPLAY }}>Performance Ads OS</div>
        <p className="mt-2 text-[12.5px] text-[#A7956E]">نظام عملي لفهم الإعلانات المدفوعة واتخاذ قرارات أفضل.</p>
        <div className="mt-4 flex items-center justify-center gap-5 text-[12px] text-[#7A6F50]">
          <a href="https://www.linkedin.com/in/ahmedassem" target="_blank" rel="noreferrer" className="hover:text-[#C9A66A] transition-colors">LinkedIn</a>
          <span>·</span>
          <a href="#" className="hover:text-[#C9A66A] transition-colors">Privacy Policy</a>
          <span>·</span>
          <a href="#" className="hover:text-[#C9A66A] transition-colors">Terms</a>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#070C18]/95 backdrop-blur border-t border-[#C9A66A]/20 p-3 flex items-center gap-3" data-testid="course-sticky-cta">
        <div className="text-[11.5px] text-[#A7956E] leading-tight">
          <div className="font-extrabold text-white">Performance Ads OS</div>
          <div>{PRICE}</div>
        </div>
        <button
          onClick={() => startCheckout("sticky")}
          disabled={loading}
          data-testid="course-sticky-btn"
          className="mr-auto bg-gradient-to-b from-[#E2C281] to-[#C9A66A] text-[#0A0F1C] font-extrabold text-[13.5px] px-5 py-3 rounded-full min-h-[48px] disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(201,166,106,0.65)]"
        >
          ابدأ الآن
        </button>
      </div>
      <div className="md:hidden h-20" aria-hidden />
    </div>
  );
}
